import React, { useState } from "react";

import { useQuery } from "@apollo/client";
import { Add as AddIcon, Search as SearchIcon } from "@mui/icons-material";
import { Typography, Box, TextField, Button, styled } from "@mui/material";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";

import { graphql } from "@/gql";
import useDebounce from "@/hooks/useDebounce";
import useKeyboardShortcut from "@/hooks/useKeyboardShortcut";
import { formatISODate } from "@/utils/datetime";

import PlanList from "./PlanList";

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 1),
}));

export const planListPageQuery = graphql(`
  query ClientPrograms(
    $clientId: ID!
    $currentDate: ISO8601Date!
    $name: String
  ) {
    currentPrograms: programs(
      clientId: $clientId
      withinDate: $currentDate
      name: $name
    ) {
      nodes {
        ...Plan
      }
    }

    upcomingPrograms: programs(
      clientId: $clientId
      upcomingAfter: $currentDate
      name: $name
    ) {
      nodes {
        ...Plan
      }
    }

    finishedPrograms: programs(
      clientId: $clientId
      finishedBefore: $currentDate
      name: $name
    ) {
      nodes {
        ...Plan
      }
    }
  }
`);

const PlanListPage: React.FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const [searchText, _setSearchText] = useState("");
  const setSearchText = useDebounce(_setSearchText, 500);
  const { data } = useQuery(planListPageQuery, {
    variables: {
      clientId: clientId ?? "",
      currentDate: formatISODate(new Date()),
      name: searchText,
    },
  });

  const createPlanScreen = {
    pathname: `${pathname}/new`,
    state: { source: pathname },
  };

  useKeyboardShortcut("Ctrl+M", () => navigate(createPlanScreen));

  return (
    <Root>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="body1" fontWeight="bold">
          Plans
        </Typography>
        <div>
          <TextField
            type="search"
            placeholder="Search..."
            onChange={(e) => setSearchText(e.target.value)}
            size="small"
            variant="outlined"
            InputProps={{
              startAdornment: <SearchIcon fontSize="small" />,
            }}
          />
          <Box marginRight={4} display="inline" />
          <Button
            component={Link}
            to={createPlanScreen}
            startIcon={<AddIcon fontSize="small" />}
          >
            Create Plan
          </Button>
        </div>
      </Box>
      <PlanList
        currentPrograms={data?.currentPrograms.nodes ?? []}
        upcomingPrograms={data?.upcomingPrograms.nodes ?? []}
        finishedPrograms={data?.finishedPrograms.nodes ?? []}
      />
    </Root>
  );
};

export default PlanListPage;
